import { strFromU8, unzipSync, zipSync } from "fflate";

import {
  CloudApiError,
  commitVersion,
  createProject,
  getProject,
  getVersionDetail,
  preflightVersion,
  type PreflightResponse,
  type VersionDetailResponse,
} from "./api";
import {
  buildEnvironmentProjectArchive,
  loadEnvironmentZip,
  type BuiltEnvironmentProjectArchive,
} from "../utils/environmentBundle";
import { migrateProjectManifest } from "../core/migrations/projectManifest";
import { validateCritterProjectArchiveCloudPreflight } from "../utils/projectUploadPreflight";
import { cacheBlobByHash, getCachedBlobByHash } from "../utils/scene-loader/blobCache";
import { logger } from "../utils/logger";
import {
  BROWSER_IMPORT_LIMITS,
  BrowserImportLimitError,
  MAXIMUM_ASSET_SIZE_MESSAGE,
  formatBytes,
} from "../utils/importLimits";

const MAX_RATE_LIMIT_RETRIES = 2;
const FALLBACK_RATE_LIMIT_RETRY_MS = 1_000;

export type CloudSyncKind = "autosave" | "named";

export type CloudSyncProjectInput = {
  id?: string;
  cloudProjectId?: string;
  name?: string;
};

type SyncOptions = {
  kind: CloudSyncKind;
  label?: string | null;
  baseVersionId?: string | null;
  shareToken?: string | null;
  buildArchive?: () => Promise<BuiltEnvironmentProjectArchive>;
  fetchImpl?: typeof fetch;
  onDiagnostics?: (diagnostics: CloudSaveDiagnostics) => void;
};

type LoadOptions = {
  fetchImpl?: typeof fetch;
  loadZip?: typeof loadEnvironmentZip;
  getCachedBlob?: typeof getCachedBlobByHash;
  cacheBlob?: typeof cacheBlobByHash;
  onDiagnostics?: (diagnostics: CloudLoadDiagnostics) => void;
};

export type CloudSaveDiagnostics = {
  direction: "save";
  projectId: string;
  kind: CloudSyncKind;
  metadataBundleBytes: number;
  manifestAssetCount: number;
  manifestAssetBytes: number;
  directUploadCount: number;
  directUploadBytes: number;
  alreadyStoredCount: number;
  alreadyStoredBytes: number;
  alreadyStoredLibraryCount: number;
  alreadyStoredLibraryBytes: number;
  alreadyStoredPrincipalCount: number;
  alreadyStoredPrincipalBytes: number;
};

export type CloudLoadDiagnostics = {
  direction: "load";
  projectId: string;
  versionId: string;
  metadataBundleBytes: number;
  manifestAssetCount: number;
  manifestAssetBytes: number;
  remoteAssetDownloadCount: number;
  remoteAssetDownloadBytes: number;
  cachedAssetCount: number;
  cachedAssetBytes: number;
  libraryAssetCount: number;
  libraryAssetBytes: number;
  principalAssetCount: number;
  principalAssetBytes: number;
  reconstructedArchiveBytes: number;
};

function defaultFetch(): typeof fetch {
  return globalThis.fetch.bind(globalThis);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function retryDelayFromHeaders(headers: Headers): number | null {
  const value = headers.get("retry-after");
  if (!value) return null;

  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;

  const retryAt = Date.parse(value);
  if (!Number.isFinite(retryAt)) return null;
  return Math.max(0, retryAt - Date.now());
}

function retryDelayFromCloudApiError(error: CloudApiError, attempt: number): number {
  if (error.retryAfterSeconds !== null) return error.retryAfterSeconds * 1000;
  return FALLBACK_RATE_LIMIT_RETRY_MS * 2 ** attempt;
}

async function withRateLimitRetry<T>(operation: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (
        !(error instanceof CloudApiError) ||
        error.status !== 429 ||
        attempt >= MAX_RATE_LIMIT_RETRIES
      ) {
        throw error;
      }

      const retryAfterMs = retryDelayFromCloudApiError(error, attempt);
      logger.warn("[cloudSync] Cloud API rate limited; retrying.", { retryAfterMs });
      await wait(retryAfterMs);
    }
  }
}

async function fetchWithRateLimitRetry(
  fetchImpl: typeof fetch,
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetchImpl(input, init);
    if (response.status !== 429 || attempt >= MAX_RATE_LIMIT_RETRIES) return response;

    const retryAfterMs =
      retryDelayFromHeaders(response.headers) ?? FALLBACK_RATE_LIMIT_RETRY_MS * 2 ** attempt;
    logger.warn("[cloudSync] Asset upload rate limited; retrying.", { retryAfterMs });
    await wait(retryAfterMs);
  }
}

function cloudSyncDiagnosticsEnabled(): boolean {
  return import.meta.env?.VITE_CLOUD_SYNC_DIAGNOSTICS === "true";
}

function emitCloudSyncDiagnostics<T extends CloudSaveDiagnostics | CloudLoadDiagnostics>(
  diagnostics: T,
  handler: ((diagnostics: T) => void) | undefined
): void {
  handler?.(diagnostics);
  if (cloudSyncDiagnosticsEnabled()) {
    logger.info("[cloudSync] diagnostics", diagnostics);
  }
}

function metadataBundleFromArchive(archive: BuiltEnvironmentProjectArchive): Blob {
  const manifest = archive.projectManifest;
  const keep = new Set([
    "project.json",
    manifest.files.projectSnapshot,
    manifest.files.model,
    manifest.files.sceneConfig,
  ]);
  const entries = Object.fromEntries(
    Object.entries(archive.archiveEntries).filter(([path]) => keep.has(path))
  );
  const bytes = zipSync(entries, { level: 0 });
  const buffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
  return new Blob([buffer], { type: "application/zip" });
}

function assetBlobFromArchive(archive: BuiltEnvironmentProjectArchive, path: string): Blob {
  const bytes = archive.archiveEntries[path];
  if (!bytes) throw new Error(`Project archive is missing asset ${path}`);
  const buffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
  return new Blob([buffer]);
}

function assertCloudProjectFitsBrowserOpenLimit(args: {
  name: string;
  metadataBundleBytes: number;
  manifestAssetBytes: number;
}): void {
  const estimatedBytes = args.metadataBundleBytes + args.manifestAssetBytes;
  if (estimatedBytes <= BROWSER_IMPORT_LIMITS.archiveFileBytes) return;
  throw new BrowserImportLimitError(
    `Cloud project "${args.name}" is ${formatBytes(
      estimatedBytes
    )}. Browser import supports project archives up to ${formatBytes(
      BROWSER_IMPORT_LIMITS.archiveFileBytes
    )}. Remove oversized project assets before saving to cloud. ${MAXIMUM_ASSET_SIZE_MESSAGE}`,
    "cloud-project-too-large",
    {
      fileName: args.name,
      actualBytes: estimatedBytes,
      limitBytes: BROWSER_IMPORT_LIMITS.archiveFileBytes,
      helpMessage: MAXIMUM_ASSET_SIZE_MESSAGE,
    }
  );
}

async function putAssetWithRetry(args: {
  upload: PreflightResponse["needsUpload"][number];
  blob: Blob;
  projectId: string;
  shareToken?: string | null;
  asset: { hash: string; size: number; contentType?: string | null };
  fetchImpl: typeof fetch;
}): Promise<void> {
  let response = await fetchWithRateLimitRetry(args.fetchImpl, args.upload.uploadUrl, {
    method: args.upload.method,
    headers: args.upload.headers,
    body: args.blob,
  });
  if (response.ok) return;

  if (response.status !== 403 && response.status !== 401) {
    throw new Error(`Asset upload failed for ${args.asset.hash}: ${response.status}`);
  }

  const refreshed = await withRateLimitRetry(() =>
    preflightVersion(args.projectId, [args.asset], {
      shareToken: args.shareToken,
    })
  );
  const retry = refreshed.needsUpload.find((item) => item.hash === args.asset.hash);
  if (!retry) return;
  response = await fetchWithRateLimitRetry(args.fetchImpl, retry.uploadUrl, {
    method: retry.method,
    headers: retry.headers,
    body: args.blob,
  });
  if (!response.ok)
    throw new Error(`Asset upload retry failed for ${args.asset.hash}: ${response.status}`);
}

export async function syncProjectToCloud(
  project: CloudSyncProjectInput,
  options: SyncOptions
): Promise<{ projectId: string; versionId: string; versionNumber: number }> {
  const archive = await (
    options.buildArchive ?? (() => buildEnvironmentProjectArchive({ extension: "critterrefs" }))
  )();
  const preflight = validateCritterProjectArchiveCloudPreflight(archive.archiveEntries);
  if (!preflight.ok || !preflight.manifest) {
    throw new Error(
      `Project is not ready for cloud sync: ${preflight.errors[0]?.message ?? "invalid archive"}`
    );
  }

  const projectId =
    project.cloudProjectId ??
    project.id ??
    (
      await withRateLimitRetry(() =>
        createProject(project.name || archive.projectManifest.projectName)
      )
    ).projectId;
  const fetchImpl = options.fetchImpl ?? defaultFetch();
  const metadataBundle = metadataBundleFromArchive(archive);
  const assets = archive.projectManifest.assets.map((asset) => ({
    hash: asset.sha256!,
    size: asset.byteLength,
    contentType: null,
  }));
  assertCloudProjectFitsBrowserOpenLimit({
    name: project.name || archive.projectManifest.projectName || projectId,
    metadataBundleBytes: metadataBundle.size,
    manifestAssetBytes: assets.reduce((sum, asset) => sum + asset.size, 0),
  });
  const assetSizeByHash = new Map(assets.map((asset) => [asset.hash, asset.size]));
  const preflightResponse = await withRateLimitRetry(() =>
    preflightVersion(projectId, assets, {
      shareToken: options.shareToken,
    })
  );

  for (const upload of preflightResponse.needsUpload) {
    const asset = archive.projectManifest.assets.find((entry) => entry.sha256 === upload.hash);
    if (!asset) throw new Error(`Upload URL returned unknown asset ${upload.hash}`);
    await putAssetWithRetry({
      upload,
      blob: assetBlobFromArchive(archive, asset.path),
      projectId,
      shareToken: options.shareToken,
      asset: { hash: upload.hash, size: asset.byteLength, contentType: null },
      fetchImpl,
    });
  }

  const alreadyStoredLibraryBytes = preflightResponse.alreadyStored
    .filter((asset) => asset.origin === "library")
    .reduce((sum, asset) => sum + (assetSizeByHash.get(asset.hash) ?? 0), 0);
  const alreadyStoredPrincipalBytes = preflightResponse.alreadyStored
    .filter((asset) => asset.origin === "principal")
    .reduce((sum, asset) => sum + (assetSizeByHash.get(asset.hash) ?? 0), 0);
  emitCloudSyncDiagnostics(
    {
      direction: "save",
      projectId,
      kind: options.kind,
      metadataBundleBytes: metadataBundle.size,
      manifestAssetCount: assets.length,
      manifestAssetBytes: assets.reduce((sum, asset) => sum + asset.size, 0),
      directUploadCount: preflightResponse.needsUpload.length,
      directUploadBytes: preflightResponse.needsUpload.reduce(
        (sum, asset) => sum + (assetSizeByHash.get(asset.hash) ?? 0),
        0
      ),
      alreadyStoredCount: preflightResponse.alreadyStored.length,
      alreadyStoredBytes: alreadyStoredLibraryBytes + alreadyStoredPrincipalBytes,
      alreadyStoredLibraryCount: preflightResponse.alreadyStored.filter(
        (asset) => asset.origin === "library"
      ).length,
      alreadyStoredLibraryBytes,
      alreadyStoredPrincipalCount: preflightResponse.alreadyStored.filter(
        (asset) => asset.origin === "principal"
      ).length,
      alreadyStoredPrincipalBytes,
    },
    options.onDiagnostics
  );

  const committed = await withRateLimitRetry(() =>
    commitVersion(
      projectId,
      {
        bundle: metadataBundle,
        manifest: {
          kind: options.kind,
          label: options.label ?? null,
          baseVersionId: options.baseVersionId ?? null,
          assets: archive.projectManifest.assets.map((asset) => ({ hash: asset.sha256! })),
        },
      },
      { shareToken: options.shareToken }
    )
  );

  return { projectId, ...committed };
}

async function fetchBlob(url: string, fetchImpl: typeof fetch): Promise<Blob> {
  const response = await fetchImpl(url);
  if (!response.ok) throw new Error(`Cloud asset fetch failed: ${response.status}`);
  return response.blob();
}

async function resolveVersionDetail(
  projectId: string,
  versionId: string | undefined
): Promise<VersionDetailResponse> {
  if (versionId) return getVersionDetail(projectId, versionId);
  const project = await getProject(projectId);
  if (!project.latestVersion) throw new Error(`Project ${projectId} has no versions`);
  return getVersionDetail(projectId, project.latestVersion.id);
}

export async function loadProjectFromVersionDetail(
  detail: VersionDetailResponse,
  projectId: string,
  options: LoadOptions = {}
): Promise<{ name: string; files: number }> {
  const fetchImpl = options.fetchImpl ?? defaultFetch();
  const getCached = options.getCachedBlob ?? getCachedBlobByHash;
  const cacheBlob = options.cacheBlob ?? cacheBlobByHash;
  const bundleBlob = await fetchBlob(detail.bundleUrl, fetchImpl);
  const bundleEntries = unzipSync(new Uint8Array(await bundleBlob.arrayBuffer()));
  const manifestBytes = bundleEntries["project.json"];
  if (!manifestBytes) throw new Error("Cloud project bundle is missing project.json");

  const manifest = migrateProjectManifest(JSON.parse(strFromU8(manifestBytes)));
  if (!manifest) throw new Error("Cloud project bundle has an invalid project manifest");
  assertCloudProjectFitsBrowserOpenLimit({
    name: detail.version.id || projectId,
    metadataBundleBytes: bundleBlob.size,
    manifestAssetBytes: manifest.assets.reduce((sum, asset) => sum + asset.byteLength, 0),
  });

  const entries = { ...bundleEntries };
  let remoteAssetDownloadCount = 0;
  let remoteAssetDownloadBytes = 0;
  let cachedAssetCount = 0;
  let cachedAssetBytes = 0;
  let libraryAssetCount = 0;
  let libraryAssetBytes = 0;
  let principalAssetCount = 0;
  let principalAssetBytes = 0;

  for (const asset of manifest.assets) {
    if (!asset.sha256) throw new Error(`Cloud asset ${asset.path} is missing SHA-256`);
    let blob = await getCached(asset.sha256);
    if (!blob) {
      const assetUrl = detail.assets[asset.sha256]?.url;
      if (!assetUrl) throw new Error(`Cloud version detail is missing URL for ${asset.sha256}`);
      blob = await fetchBlob(assetUrl, fetchImpl);
      remoteAssetDownloadCount += 1;
      remoteAssetDownloadBytes += blob.size;
      await cacheBlob(asset.sha256, blob).catch((error) => {
        logger.warn("[cloudSync] Failed to cache cloud asset by hash.", error);
      });
    } else {
      cachedAssetCount += 1;
      cachedAssetBytes += blob.size;
    }

    const origin = detail.assets[asset.sha256]?.origin;
    if (origin === "library") {
      libraryAssetCount += 1;
      libraryAssetBytes += asset.byteLength;
    } else if (origin === "principal") {
      principalAssetCount += 1;
      principalAssetBytes += asset.byteLength;
    }
    entries[asset.path] = new Uint8Array(await blob.arrayBuffer());
  }

  const zipBytes = zipSync(entries, { level: 0 });
  const zipBuffer = zipBytes.buffer.slice(
    zipBytes.byteOffset,
    zipBytes.byteOffset + zipBytes.byteLength
  ) as ArrayBuffer;
  emitCloudSyncDiagnostics(
    {
      direction: "load",
      projectId,
      versionId: detail.version.id,
      metadataBundleBytes: bundleBlob.size,
      manifestAssetCount: manifest.assets.length,
      manifestAssetBytes: manifest.assets.reduce((sum, asset) => sum + asset.byteLength, 0),
      remoteAssetDownloadCount,
      remoteAssetDownloadBytes,
      cachedAssetCount,
      cachedAssetBytes,
      libraryAssetCount,
      libraryAssetBytes,
      principalAssetCount,
      principalAssetBytes,
      reconstructedArchiveBytes: zipBytes.byteLength,
    },
    options.onDiagnostics
  );
  const file = new File([zipBuffer], `${projectId}.critterrefs`, { type: "application/zip" });
  const loaded = await (options.loadZip ?? loadEnvironmentZip)(file);
  if (!loaded || loaded.files <= 0) {
    throw new Error("Cloud project load was blocked before replacing the current scene.");
  }
  return loaded;
}

export async function loadProjectFromCloud(
  projectId: string,
  versionId?: string,
  options: LoadOptions = {}
): Promise<{ name: string; files: number }> {
  const detail = await resolveVersionDetail(projectId, versionId);
  return loadProjectFromVersionDetail(detail, projectId, options);
}
